import React, { FC, PropsWithChildren, useMemo, useState } from 'react'
import styles from '../SearchProvider.module.scss'
import SALink from 'components/SALink'
import FormRadio from 'components/SA/FormElements/Radio'
import filterStyles from '../../Filters/Filters.module.scss'
import classNames from 'classnames'
import {
  CustomHandleUpdateSelectedFacetParams,
  FacetOptionObject,
  FacetServiceApi,
  UseReduxSearchProviderResponse,
} from '../types'
import { getHrefFunction, updateClientSideUrl } from '../helpers/url'
import CloseIcon from 'svg/SA/close-small.svg'
import useReduxSearchProvider from '../hooks/useReduxSearchProvider'

type FilterOptionsObject = {
  text: string
  selected: boolean
  onClick: () => void
  href: string
  isHidden?: boolean
}

type RadioListFacetProps = {
  baseUrl: string
  nextAppRoute: string
  facetService: FacetServiceApi
  facetName: string
  facetOptions?: ReadonlyArray<FacetOptionObject>
  customFilterOptions?: ReadonlyArray<FilterOptionsObject>
  maxDisplayedItems?: number
  isQueryParam?: boolean
  defaultValue?: string
  customHandleUpdateSelectedFacet?: (params: CustomHandleUpdateSelectedFacetParams) => void
  customClickFacetOption?: (params: CustomHandleUpdateSelectedFacetParams) => void
  useReduxProviderHook?: () => UseReduxSearchProviderResponse
}

const RadioListFacet: FC<PropsWithChildren<RadioListFacetProps>> = ({
  baseUrl,
  nextAppRoute,
  facetService,
  facetName,
  facetOptions = [],
  customFilterOptions,
  maxDisplayedItems = 6,
  isQueryParam,
  defaultValue,
  useReduxProviderHook = useReduxSearchProvider,
  customHandleUpdateSelectedFacet,
  customClickFacetOption,
  children,
}) => {
  const [isShowMoreOpen, setIsShowMoreOpen] = useState(false)

  const {
    selectedFacets,
    basicQueryParams,
    pathParams,
    sharedFacetNames,
    pageParams,
  } = useReduxProviderHook()

  const handleUpdateSelectedFacets = ({
    newSelectedFacets,
    newQueryParams,
    facetName,
    facetValue,
  }) => {
    const newSelectedFacetsAndQueryParams = { ...newSelectedFacets, ...newQueryParams }
    const href = getHrefFunction({
      facetsAndQueryParams: newSelectedFacetsAndQueryParams,
      pathParams,
      sharedFacetNames,
    })()
    const newUrl = `${baseUrl}${href}`

    if (Boolean(customHandleUpdateSelectedFacet)) {
      customHandleUpdateSelectedFacet?.({
        newSelectedFacets: newSelectedFacetsAndQueryParams,
        newUrl,
        facetName,
        facetValue,
      })
      return
    }

    customClickFacetOption?.({
      newSelectedFacets: newSelectedFacetsAndQueryParams,
      newUrl,
      facetName,
      facetValue,
    })
    updateClientSideUrl(newUrl, nextAppRoute)
  }

  const handleToggleShowMore = () => {
    setIsShowMoreOpen((prevValue) => !prevValue)
  }

  const filterOptions: ReadonlyArray<FilterOptionsObject> =
    customFilterOptions ||
    facetOptions.map(({ label, value }, index) => {
      const href = getHrefFunction({
        facetsAndQueryParams: { [facetName]: [label], page: [''] },
        pathParams,
        sharedFacetNames,
      })()
      const newHref = `${baseUrl}${href}`

      return {
        text: label,
        selected: facetService.getIsFacetSelected({
          selectedFacets,
          pageParams,
          facetValue: value,
          facetName,
        }),
        onClick: () =>
          facetService.handleClickFacet({
            facetValue: value,
            facetName,
            selectedFacets,
            basicQueryParams,
            updateSelectedFacetsAndQueryParams: handleUpdateSelectedFacets,
            inputType: 'single',
            isQueryParam,
          }),
        isHidden: index >= maxDisplayedItems,
        href: newHref,
      }
    })

  const hasHiddenOption = useMemo(() => filterOptions.some(({ isHidden }) => Boolean(isHidden)), [
    filterOptions,
  ])

  return (
    <div className={styles.radioListContainer}>
      {!filterOptions.length ? (
        <p className='defaultTypographySmall'>No Options Available</p>
      ) : (
        <div className={filterStyles.listFilterWrapper}>
          <div className={filterStyles.listFilterContainer}>
            {filterOptions.map(({ href, text, onClick, selected, isHidden }, index) => (
              <div
                className={classNames(filterStyles.radioItemBox, {
                  [filterStyles.radioItemBoxHidden]: isHidden && !isShowMoreOpen,
                })}
                key={`${text}-${index}`}
              >
                <SALink
                  href={href}
                  tabIndex={-1}
                  data-gtm-event='filter'
                  data-gtm-info={`${facetName} - ${text}`}
                  className={filterStyles.filterItemLink}
                >
                  <FormRadio
                    key={text}
                    name={text}
                    text={text}
                    onClick={onClick}
                    checked={selected}
                    aria-label={`Select ${text}`}
                  />
                </SALink>
                <CloseIcon
                  onClick={onClick}
                  className={classNames(filterStyles.radioItemCloseIcon, {
                    [filterStyles.radioItemCloseIconHidden]: !selected || text === defaultValue,
                  })}
                />
              </div>
            ))}
          </div>
          {hasHiddenOption && (
            <div className={filterStyles.collapsableFilterShowMore}>
              <button onClick={handleToggleShowMore}>
                {isShowMoreOpen ? 'Show Less' : 'Show More'}
              </button>
            </div>
          )}

          {children}
        </div>
      )}
    </div>
  )
}

export default RadioListFacet
